import { type FormEvent, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import ModalComponent from 'components/Common/Modal';
import createNotification from 'components/Settings/Utilities/CreateNotification';
import useFeed from 'api/hooks/useFeed';

interface RequestCustomSourcePopupProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

const RequestCustomSourcePopup = ({ isOpen, setIsOpen }: RequestCustomSourcePopupProps) => {
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [isSending, setIsSending] = useState(false);
  const { sendCustomSourceRequest } = useFeed();

  const inputElement = useRef<HTMLInputElement>(null);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSending(true);
    try {
      await sendCustomSourceRequest({ sourceName: name.trim(), url: url.trim() });
      createNotification('success', 'Source requested');
      setIsOpen(false);
      setName('');
      setUrl('');
    } catch {
// apiClient's error interceptor already shows a toast
    } finally {
      setIsSending(false);
    }
  };

  useEffect(() => {
    setTimeout(() => {
      if (inputElement?.current) {
        inputElement.current?.focus();
      }
    }, 300);
  }, [isOpen]);

  return (
    <ModalComponent maxWidth={640} isOpen={isOpen} setIsOpen={setIsOpen}>
      <div className='pt-2 pb-4'>
        <div className='section-title pb-3 specific-x-padding-28 pt-3'>
          <h3 className='topic-title mb-0'>Request a custom source</h3>
        </div>
        <form className='specific-x-padding-28 pt-3' onSubmit={handleSubmit}>
          <p className='main-subtle-text mb-4'>
            We review every request and aim to add all sources relevant to policy and public affairs, subject to
            copyright, licensing and technical restrictions. Review and setup timeframes depend on your{' '}
            <Link to='/pricing' className='simple-link-button text-underline'>
              subscription type
            </Link>
            .
          </p>
          <label htmlFor='custom-source-name' className='font-weight-bold'>
            Source name
          </label>
          <input
            ref={inputElement}
            id='custom-source-name'
            className='main-input form-control mb-4'
            placeholder='e.g. UNICEF press releases'
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <label htmlFor='custom-source-url' className='font-weight-bold'>
            URL
          </label>
          <input
            id='custom-source-url'
            className='main-input form-control'
            placeholder='e.g. unicef.org/media/press-releases'
            value={url}
            onChange={(e) => setUrl(e.target.value)}
          />
          <div className='d-flex justify-content-end pt-5 pb-3'>
            <button
              type='submit'
              className='general-button rounded-pill action-button px-4 py-2'
              disabled={!name.trim() || !url.trim() || isSending}
            >
              Request source
            </button>
          </div>
        </form>
      </div>
    </ModalComponent>
  );
};

export default RequestCustomSourcePopup;
